import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, Mail, RefreshCw, FileText, AlertCircle } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { de } from "date-fns/locale";

/**
 * Phase 13 — Weekly digest preview + optional test-send.
 */
export default function Digest() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mailStatus, setMailStatus] = useState(null);
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, m] = await Promise.all([
        api.get("/digest/preview"),
        api.get("/digest/mail-status"),
      ]);
      setDigest(d.data); setMailStatus(m.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Konnte Digest nicht laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!to.trim()) return toast.error("Bitte Empfänger-E-Mail angeben");
    setSending(true);
    try {
      const { data } = await api.post("/digest/send-test", { to: to.trim() });
      if (data.ok) toast.success(data.message); else toast.error(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Senden fehlgeschlagen");
    } finally { setSending(false); }
  };

  if (loading && !digest) return <div className="p-10 text-slate-400">Lade Digest…</div>;

  return (
    <div className="p-6 lg:p-10 max-w-[1100px] mx-auto" data-testid="digest-page">
      <header className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Berichte</div>
          <h1 className="text-3xl font-extrabold tracking-tight mt-1 text-white">Wochen-Digest</h1>
          <p className="text-sm text-slate-400 mt-2">Montag 07:30 — die letzten 7 Tage im Überblick. Immer in der App einsehbar, optional als E-Mail.</p>
        </div>
        <Button onClick={load} variant="outline" className="border-white/10 bg-transparent text-slate-300 hover:bg-white/5" data-testid="digest-refresh">
          <RefreshCw className="h-4 w-4 mr-2" /> Aktualisieren
        </Button>
      </header>

      {digest && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="digest-counts">
            {[
              { k: "reports", label: "Neue Berichte" },
              { k: "open_proposals", label: "Offene Vorschläge" },
              { k: "social_posts", label: "Social-Beiträge" },
              { k: "skill_failures", label: "Skill-Fehler" },
            ].map((c) => (
              <Card key={c.k} className="bg-[#111827] border-white/5">
                <CardContent className="p-4">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">{c.label}</div>
                  <div className="text-3xl font-bold text-white mt-1" data-testid={`digest-count-${c.k}`}>{digest.counts?.[c.k] ?? 0}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="bg-[#111827] border-white/5 mb-6">
            <CardContent className="p-5">
              <div className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-emerald-400" /> Berichte
              </div>
              {digest.reports?.length ? (
                <ul className="space-y-3" data-testid="digest-reports">
                  {digest.reports.map((r) => (
                    <li key={r.id} className="border-l-2 border-emerald-500/30 pl-3">
                      <div className="text-sm text-white">{r.title}</div>
                      {r.summary && <div className="text-xs text-slate-400 mt-0.5">{r.summary}</div>}
                      {r.created_at && (
                        <div className="text-[10px] text-slate-500 mt-1">
                          vor {formatDistanceToNow(parseISO(r.created_at), { locale: de })}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-slate-400" data-testid="digest-no-reports">Keine neuen Berichte diese Woche.</div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#111827] border-white/5">
            <CardContent className="p-5">
              <div className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4 text-emerald-400" /> Per E-Mail erhalten
              </div>
              {mailStatus?.configured ? (
                <>
                  <div className="text-xs text-emerald-400 mb-3" data-testid="digest-mail-ok">
                    E-Mail-Versand ist eingerichtet · Absender: {mailStatus.from}
                  </div>
                  <div className="flex gap-2">
                    <Input value={to} onChange={(e) => setTo(e.target.value)}
                      placeholder="empfaenger@firma.de" type="email"
                      className="bg-white/5 border-white/10 text-slate-100"
                      data-testid="digest-test-to" />
                    <Button onClick={sendTest} disabled={sending}
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                      data-testid="digest-test-send">
                      {sending ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sende…</> : "Test-Mail senden"}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300" data-testid="digest-mail-not-configured">
                  <AlertCircle className="h-4 w-4 inline mr-1" />
                  E-Mail ist nicht konfiguriert. Der Owner muss <code className="text-amber-200">SMTP_HOST</code>, <code>SMTP_PORT</code>, <code>SMTP_FROM</code> (optional <code>SMTP_USER</code>/<code>SMTP_PASS</code>) in <code>/app/backend/.env</code> hinterlegen. Der Digest ist weiterhin hier in der App verfügbar.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
